/**
 * Everything here is draft state: the questionnaire answers are only written on `onSave`;
 * `onClose` discards the draft. BMI thresholds and status copy come from BMISection helpers,
 * so the modal can never disagree with the BMI question.
 */

import React, { useEffect, useMemo, useState } from "react";
import * as translator from "../../../utils/translator";
import VariantBModalShell from "./VariantBModalShell";
import { useFunnel, useShellIdFn } from "./shellContext";
import PrimaryCtaButton from "./PrimaryCtaButton";
import DatePickerWrapper from "../../../components/DatePickerWrapper/DatePickerWrapper";
import DecimalInput from "../../../components/DecimalInput/DecimalInput";
import { formatDecimal } from "../../../utils/decimalInput";
import Significance from "../components/Significance/Significance";
import { calculateBMI, getBmiStatusMessage } from "../components/BMISection/BMISection";
import { BmiInputCheck, checkBmiInput } from "../components/BMISection/bmiLimits";
import {
  calculateAge,
  getHealthProfileEditState,
  HealthProfileEditState,
  isHealthProfileSavable,
  isWithinRule,
  parseMeasurement,
} from "./healthSummaryHelpers";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

export interface HealthProfileDraft {
  /** yyyy-mm-dd, the same shape the birthday answer is stored in. */
  birthday: string;
  heightCm: number;
  weightKg: number;
  bmi: number;
  state: HealthProfileEditState;
}

interface MeasurementRule {
  min_value?: number;
  max_value?: number;
}

interface HealthProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (draft: HealthProfileDraft) => void;
  language?: string;
  /** Current answers, prefilled on open. Birthday is yyyy-mm-dd. */
  birthday: string | null;
  heightCm: number | null;
  weightKg: number | null;
  heightRule?: MeasurementRule | null;
  weightRule?: MeasurementRule | null;
  /** DE realistic-entry limits (usesBmiInputLimits); the UK keeps only the recipe rule. */
  inputLimits?: boolean;
  comorbidityThreshold: number;
  minimumThreshold: number;
  /** Whether the (already answered) comorbidity question currently names an illness. */
  hasComorbidity: boolean;
}

const MODAL_ID = "wl2_health_profile_edit_modal";

const STATE_LEVEL: Record<HealthProfileEditState, "success" | "warning" | "danger" | null> = {
  incomplete: null,
  underage: "danger",
  "not-eligible": "danger",
  conditional: "warning",
  eligible: "success",
};

const HealthProfileEditModal: React.FC<HealthProfileEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  language,
  birthday,
  heightCm: currentHeightCm,
  weightKg: currentWeightKg,
  heightRule,
  weightRule,
  inputLimits = false,
  comorbidityThreshold,
  minimumThreshold,
  hasComorbidity,
}) => {
  // Portaled sibling of the page body, so it mounts once; scoping keeps the id contract uniform.
  const scopeId = useShellIdFn();
  // HL-12357: WL3 slides the edit pop-up in.
  const isWl3 = useFunnel() === "wl3";
  const [draftBirthday, setDraftBirthday] = useState<string>(birthday ?? "");
  const [heightText, setHeightText] = useState("");
  const [weightText, setWeightText] = useState("");

  // Fresh draft on every open, prefilled with the current answers.
  useEffect(() => {
    if (!isOpen) return;
    setDraftBirthday(birthday ?? "");
    setHeightText(currentHeightCm ? formatDecimal(currentHeightCm, language) : "");
    setWeightText(currentWeightKg ? formatDecimal(currentWeightKg, language) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const heightCm = parseMeasurement(heightText);
  const weightKg = parseMeasurement(weightText);
  const heightCheck: BmiInputCheck = inputLimits ? checkBmiInput(heightText, "height") : "ok";
  const weightCheck: BmiInputCheck = inputLimits ? checkBmiInput(weightText, "weight") : "ok";
  const heightInRange = isWithinRule(heightCm, heightRule) && heightCheck !== "hard";
  const weightInRange = isWithinRule(weightKg, weightRule) && weightCheck !== "hard";

  const bmi = useMemo(
    () => (heightInRange && weightInRange && heightCm && weightKg ? calculateBMI(weightKg, heightCm) : null),
    [heightCm, weightKg, heightInRange, weightInRange]
  );
  const age = useMemo(() => calculateAge(draftBirthday || null), [draftBirthday]);

  const state = getHealthProfileEditState({ bmi, age, hasComorbidity, comorbidityThreshold, minimumThreshold });
  const canSave = isHealthProfileSavable(state) && !!draftBirthday;

  const statusMessage = useMemo(() => {
    if (state === "incomplete") return null;
    if (state === "underage") return t(language, "health_summary_edit_underage");
    const eligibility = state === "eligible" ? "eligible" : state === "conditional" ? "conditional" : "not-eligible";
    const message = getBmiStatusMessage(language, bmi, eligibility, hasComorbidity, comorbidityThreshold, minimumThreshold);
    // The shared helper prints the BMI with a dot; show it with the funnel language's decimal separator (28,4 in DE).
    if (message && bmi !== null) {
      return message.replace(String(bmi), bmi.toLocaleString(language || "de", { maximumFractionDigits: 1 }));
    }
    return message;
  }, [state, bmi, hasComorbidity, comorbidityThreshold, minimumThreshold, language]);
  const statusLevel = STATE_LEVEL[state];

  const fieldMessage = (value: number | null, inRange: boolean, check: BmiInputCheck, rule?: MeasurementRule | null): string | undefined => {
    if (check === "ok" && (value === null || inRange)) return undefined;
    // Without input limits (UK) the field keeps showing the recipe range, as before HL-12712.
    return inputLimits ? t(language, "bmi_check_value") : `${rule?.min_value ?? ""}–${rule?.max_value ?? ""}`;
  };

  const handleSave = () => {
    if (!canSave || bmi === null || heightCm === null || weightKg === null) return;
    onSave({ birthday: draftBirthday, heightCm, weightKg, bmi, state });
  };

  return (
    <VariantBModalShell
      modalId={MODAL_ID}
      isOpen={isOpen}
      onClose={onClose}
      maxWidthPx={420}
      shouldBackgroundClickHideModal={false}
      animateIn={isWl3}
    >
      <h2 id={scopeId(`${MODAL_ID}_title`)} className="font-['Poppins'] font-medium text-[18px] leading-[1.3] text-[#08080a] m-0 pr-8 mb-6">
        {t(language, "health_summary_edit_profile_title")}
      </h2>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2 w-full">
          <label
            htmlFor={scopeId(`${MODAL_ID}_birthday`)}
            className="font-['Poppins'] text-[14px] font-normal text-[#616975] leading-none"
          >
            {t(language, "health_summary_edit_birthday_label")}
          </label>
          {/* DatePickerWrapper.css pins .react-datepicker__input-container to 42px (!important);
              the input is height:100% of it, so the container is what sets the 60px field. */}
          <div className="[&_.react-datepicker__input-container]:!h-[60px] [&_input]:rounded-[12px]">
            <DatePickerWrapper
              id={scopeId(`${MODAL_ID}_birthday`)}
              value={draftBirthday}
              errorMode={!draftBirthday}
              isBirthdayInput
              localizeWeekdays
              onChange={(value: string | undefined) => setDraftBirthday(value ?? "")}
            />
          </div>
        </div>

        <DecimalInput
          id={scopeId(`${MODAL_ID}_height`)}
          label={t(language, "health_summary_edit_height_label")}
          unit={t(language, "health_summary_edit_height_placeholder")}
          value={heightText}
          onChange={setHeightText}
          message={fieldMessage(heightCm, heightInRange, heightCheck, heightRule)}
          tone={heightInRange ? "warning" : "error"}
        />

        <DecimalInput
          id={scopeId(`${MODAL_ID}_weight`)}
          label={t(language, "health_summary_edit_weight_label")}
          unit={t(language, "health_summary_edit_weight_placeholder")}
          value={weightText}
          onChange={setWeightText}
          message={fieldMessage(weightKg, weightInRange, weightCheck, weightRule)}
          tone={weightInRange ? "warning" : "error"}
        />

        {statusMessage && statusLevel && (
          <div id={scopeId(`${MODAL_ID}_status`)} data-state={state}>
            <Significance
              level={statusLevel}
              text={statusMessage}
              fontWeight="500"
              fontSize="14px"
              dangerSignificancesRefsDispatch={() => {}}
              parentID={null}
              margin="0"
            />
          </div>
        )}

        <div className="flex flex-col gap-2 w-full">
          <PrimaryCtaButton
            id={`${MODAL_ID}_save`}
            label={t(language, "health_summary_edit_save")}
            onClick={handleSave}
            disabled={!canSave}
          />
          <button
            id={scopeId(`${MODAL_ID}_cancel`)}
            type="button"
            onClick={onClose}
            className="w-full h-[54px] rounded-[16px] bg-white border border-solid border-[#f08e00] text-[#f08e00] font-['Poppins'] font-semibold text-base cursor-pointer hover:opacity-90 active:scale-[0.96] transition-all duration-100"
          >
            {t(language, "health_summary_edit_close")}
          </button>
        </div>
      </div>
    </VariantBModalShell>
  );
};

export default HealthProfileEditModal;
